import { convertFiles } from './filesConverter';

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp']);

/**
 * Downloads and converts files from an invention document entry into the standard
 * file attachment contract: { id, name, mimeType, type, content, source, metadata }
 *
 * Strategy depends on `fileType`:
 *  - 'text'  — only download .txt files (no fallback)
 *  - 'image' — only download image files (no fallback)
 *  - 'auto'  — prefer .txt → images → converter fallback (for chat attachments)
 *
 * @param {object} doc - Invention document with a `files` array
 * @param {object} [options]
 * @param {string} [options.organizationId] - Passed to convertFiles for the fallback path
 * @param {'text'|'image'|'auto'} [options.fileType='auto'] - Which file types to download
 */
export async function downloadInventionDocEntries(doc, { organizationId, fileType = 'auto', signal } = {}) {
  const token = localStorage.getItem('PATENT_HUB_AUTH_TOKEN');

  const download = async fileEntry => {
    const res = await fetch(`/api/filesmanager/files/${fileEntry.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      signal
    });
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    return res;
  };

  // --- text only ---
  if (fileType === 'text') {
    const txtFile = doc.files.find(f => (f.extension || '').toLowerCase() === 'txt');
    if (!txtFile) return [];
    const res = await download(txtFile);
    const content = await res.text();
    return [
      {
        id: String(txtFile.id),
        name: txtFile.name,
        mimeType: 'text/plain',
        type: 'text',
        content,
        source: 'attachment',
        metadata: txtFile.metadata ?? {}
      }
    ];
  }

  // --- image only ---
  if (fileType === 'image') {
    const imageFiles = doc.files.filter(f => IMAGE_EXTS.has((f.extension || '').toLowerCase()));
    if (!imageFiles.length) return [];
    const results = await Promise.allSettled(
      imageFiles.map(async f => {
        const res = await download(f);
        const blob = await res.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const mimeType = blob.type || f.mimetype || 'image/png';
        const content = base64.startsWith('data:') ? base64.split(',')[1] : base64;
        return { id: String(f.id), name: f.name, mimeType, type: 'base64', content, source: 'attachment', metadata: f.metadata ?? {} };
      })
    );
    return results.filter(r => r.status === 'fulfilled').map(r => r.value);
  }

  // --- auto: prefer .txt → images → converter fallback (for chat attachments) ---

  // 1. Prefer .txt
  const txtFile = doc.files.find(f => (f.extension || '').toLowerCase() === 'txt');
  if (txtFile) {
    const res = await download(txtFile);
    const content = await res.text();
    return [
      {
        id: String(txtFile.id),
        name: txtFile.name,
        mimeType: 'text/plain',
        type: 'text',
        content,
        source: 'attachment',
        metadata: txtFile.metadata ?? {}
      }
    ];
  }

  // 2. Images (auto)
  const imageFiles = doc.files.filter(f => IMAGE_EXTS.has((f.extension || '').toLowerCase()));
  if (imageFiles.length) {
    const results = await Promise.allSettled(
      imageFiles.map(async f => {
        const res = await download(f);
        const blob = await res.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const mimeType = blob.type || f.mimetype || 'image/png';
        const content = base64.startsWith('data:') ? base64.split(',')[1] : base64;
        return { id: String(f.id), name: f.name, mimeType, type: 'base64', content, source: 'attachment', metadata: f.metadata ?? {} };
      })
    );
    const entries = results.filter(r => r.status === 'fulfilled').map(r => r.value);
    if (entries.length) return entries;
  }

  // 3. Fallback (auto) — parse via converter (pdf, docx, etc.)
  const original = doc.files[0];
  if (!original) return [];
  const res = await download(original);
  const blob = await res.blob();
  const browserFile = new File([blob], original.name, { type: original.mimetype || blob.type });
  const result = await convertFiles([browserFile], { organizationId });
  return result
    .filter(e => !e.error)
    .flatMap(e => {
      if (e.type === 'pages' && Array.isArray(e.content)) {
        return e.content.map(page => ({
          ...page,
          id: page.id ?? e.id ?? String(original.id),
          source: 'attachment',
          metadata: e.metadata ?? original.metadata ?? {}
        }));
      }
      return [{ ...e, id: e.id ?? String(original.id), source: 'attachment', metadata: e.metadata ?? original.metadata ?? {} }];
    });
}
